import React from 'react';
import { RouterProvider } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { ChatbotProvider } from './contexts/ChatbotContext';
import router from './routes';
import Chatbot from './components/Chatbot/Chatbot';
import './index.css';
import './styles/global.css';
import './styles/swiper.css';
import { ToastProvider } from './contexts/ToastContext';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import { Button } from './components/ui/button';
import { networkUtils } from './config/firebase';

const queryClient = new QueryClient();

// Add fallback component for error boundary with network status
const GlobalFallback = ({ error, resetErrorBoundary }: FallbackProps) => {
  const [isOnline, setIsOnline] = React.useState(networkUtils.isOnline());

  React.useEffect(() => {
    return networkUtils.addNetworkStatusListener(setIsOnline);
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full mx-auto bg-white p-8 rounded-lg shadow-md">
        <h2 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h2>
        <p className="text-gray-700 mb-6">
          {!isOnline ? (
            'You are currently offline. Please check your internet connection and try again.'
          ) : (
            'The application encountered an error and couldn\'t continue. This could be due to:'
          )}
        </p>
        {isOnline && (
          <ul className="list-disc pl-6 mb-6 text-gray-600">
            <li>A temporary network issue</li>
            <li>A data loading problem</li>
            <li>An unexpected application error</li>
          </ul>
        )}
        <div className="space-y-4">
          <Button 
            onClick={resetErrorBoundary}
            className="w-full"
            disabled={!isOnline}
          >
            {isOnline ? 'Reload and Try Again' : 'Waiting for Connection...'}
          </Button>
          <p className="text-sm text-gray-500 text-center">
            {isOnline 
              ? 'If the problem persists, please contact support.'
              : 'Please check your internet connection and try again.'}
          </p>
        </div>
      </div>
    </div>
  );
};

// Custom error handler function with enhanced logging
const logErrorToConsole = (error: Error, info: React.ErrorInfo) => {
  console.error('Application error caught by error boundary:', error);
  console.error('Component stack:', info.componentStack);
  
  // Log additional context if available
  if (error instanceof Error) {
    console.error('Error name:', error.name);
    console.error('Error message:', error.message);
    console.error('Error stack:', error.stack);
  }
};

// Provider wrapper component to handle provider initialization errors
const ProviderWrapper: React.FC<{children: React.ReactNode}> = ({ children }) => {
  const [hasError, setHasError] = React.useState(false);

  if (hasError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full mx-auto bg-white p-8 rounded-lg shadow-md">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Provider Initialization Error</h2>
          <p className="text-gray-700 mb-6">
            There was an error initializing the application providers. Please try refreshing the page.
          </p>
          <Button 
            onClick={() => window.location.reload()}
            className="w-full"
          >
            Refresh Page
          </Button>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary
      FallbackComponent={({ error }) => {
        setHasError(true);
        return (
          <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="max-w-md w-full mx-auto bg-white p-8 rounded-lg shadow-md">
              <h2 className="text-2xl font-bold text-red-600 mb-4">Provider Error</h2>
              <p className="text-gray-700 mb-6">
                Error initializing providers: {error.message}
              </p>
              <Button 
                onClick={() => window.location.reload()}
                className="w-full"
              >
                Refresh Page
              </Button>
            </div>
          </div>
        );
      }}
    >
      {children}
    </ErrorBoundary>
  );
};

const App: React.FC = () => {
  return (
    <ErrorBoundary 
      FallbackComponent={GlobalFallback}
      onError={logErrorToConsole}
      onReset={() => {
        // Clear any cached state that might be causing errors
        window.localStorage.removeItem('lastErrorTimestamp');
        // Clear any other cached data that might be causing issues
        window.sessionStorage.clear();
      }}
    >
      <ProviderWrapper>
        <ToastProvider>
          <AuthProvider>
            <ChatbotProvider>
              <RouterProvider router={router} />
              <Chatbot />
              <Toaster 
                position="top-center"
                toastOptions={{
                  duration: 5000,
                  style: {
                    background: '#363636',
                    color: '#fff',
                  },
                  success: {
                    duration: 3000
                  },
                  error: {
                    duration: 4000
                  },
                }}
              />
            </ChatbotProvider>
          </AuthProvider>
        </ToastProvider>
      </ProviderWrapper>
    </ErrorBoundary>
  );
};

export default App; 